import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import {
  EModule,
  EThemeKey,
  getNavigateTo,
  getThemeOptions,
  UIModulesProvider,
  useIntlMessages,
  useSocketEventsHandler,
} from '@lumahealthhq/ui-common';
import { EModelEnv, useCurrentEnv, useSetCurrentAuth, useSetSocket, useSocket } from '@lumahealthhq/ui-model';
import translationMessages from 'i18n/translationMessages';

import { changeLocale, initApp, showMessage } from '@lib/actions';
import { useCurrentLocale, useCurrentMaster, useCurrentOrPrevUser } from '@lib/hooks';
import { selectAppReady } from '@lib/selectors';
import configService, { getConfigString } from '@lib/services/configService';
import { KEYS } from '@lib/services/storageService';
import axiosClient from '@lib/utils/axiosClient';

import { useStylesPatientAppRedesign } from 'hooks';
import { getSocket } from 'utils/socket';

const routingMap = {
  [EModule.ContainerAvailabilitiesPicker]: '/availabilities',
  [EModule.ContainerAvailabilityBook]: '/book',
  [EModule.ChatPatient]: '/main/chat',
  [EModule.ContainerAuthPatientLogin]: '/signup',
  [EModule.ContainerPatientFormLaunch]: '/survey',
  [EModule.ContainerPatientFormTemplateLaunch]: '/survey',
};

const keyEnv = 'patient-web-app:env';
const env = configService.getPodName() as EModelEnv;

function parseEnvVarString(value?: string) {
  return `${value}`.trim();
}

function parseEnvVarArray(value?: string) {
  return `${value}`.split(',').map((v) => v.trim());
}

function parseEnvVarInt(value?: string) {
  return parseInt(`${value}`, 10);
}

const parseEnvJSON = (value?: string, defaultValue?: any) => {
  try {
    return JSON.parse(value || JSON.stringify(defaultValue));
  } catch (error) {
    return defaultValue;
  }
};

const config = {
  podName: parseEnvVarString(configService.getPodName()),
  api: {
    base: parseEnvVarString(configService.getApiBasePath()),
    embedded: parseEnvVarString(configService.getApiEmbeddedPath()),
    graphql: parseEnvVarString(configService.getApiGraphQLPath()),
    socket: parseEnvVarString(configService.getApiSocketPath()),
  },
  broadcast: {
    invalidTerms: parseEnvVarArray(configService.getBroadcastInvalidTerms()),
    offensiveWords: parseEnvVarArray(configService.getBroadcastOffensiveWords()),
  },
  files: {
    accept: parseEnvVarString(configService.getFilesAccept()),
  },
  fullstory: {
    id: parseEnvVarString(configService.getFullstoryId()),
  },
  logger: {
    baseUrl: parseEnvVarString(getConfigString('REACT_APP_LOGGER_BASE_URL')),
  },
  looker: {
    url: parseEnvVarString(configService.getLookerUrl()),
  },
  googl: {
    key_maps: parseEnvVarString(configService.getGooglKeyMaps()),
    conversion: {
      partner_id: parseEnvVarString(getConfigString('REACT_APP_GOOGLE_CONVERSION_PARTNER_ID')),
      api: parseEnvVarString(getConfigString('REACT_APP_GOOGLE_CONVERSION_API')),
    },
  },
  gtm: {
    id: parseEnvVarString(configService.getGtmId()),
  },
  recaptcha: {
    id: parseEnvVarString(configService.getRecaptchaId()),
  },
  site: {
    next: parseEnvVarString(configService.getSiteNext()),
    patient: parseEnvVarString(configService.getSitePatient()),
    widgets: parseEnvVarString(configService.getSiteWidgets()),
  },
  web: {
    passwordMinimumLength: parseEnvVarInt(configService.getWebPasswordMinimumLength()),
  },
  resellerPartners: parseEnvJSON(configService.getResellerPartners(), []),
};

function SocketHandler() {
  // Handle queries invalidation on socket events for federated modules
  useSocketEventsHandler();
  return null;
}

function AuthHandler() {
  const setAuth = useSetCurrentAuth();
  const currentUser = useCurrentOrPrevUser();
  const currentMaster = useCurrentMaster();
  const [, setEnv] = useCurrentEnv();

  // Reuse the patient-web-app auth for federated modules
  useEffect(() => {
    setAuth({ currentMaster, currentUser } as any);

    // WORKAROUND force the env as ui-packages still uses the localStorage
    // instead of the context's value
    // We're using the same effect than auth to prevent racing conditions
    setEnv(env);
  }, [currentMaster, currentUser, setAuth, setEnv]);

  // Reuse the patient-web-app socket for federated modules
  const socket = useSocket();
  const setSocket = useSetSocket();
  useEffect(() => {
    // Listening for `currentUser` changes bc we can't listen for `socket` changes
    // (the socket in patient-web-app is created using a saga instead of a hook)
    if (currentUser && !socket) setSocket(getSocket());
  }, [currentUser, socket, setSocket]);

  return socket ? <SocketHandler /> : null;
}

export interface ModulesProviderProps {
  children: React.ReactNode;
}

export default function ModulesProvider({ children }: ModulesProviderProps) {
  const locale = useCurrentLocale() as any;
  const dispatch = useDispatch();
  const intlMessages = useIntlMessages(locale);
  const stylesPatientAppRedesign = useStylesPatientAppRedesign();
  const appReady = useSelector(selectAppReady);

  const messages = useMemo(
    () => ({
      // @ts-ignore
      ...translationMessages[locale],
      ...intlMessages,
    }),
    [locale, intlMessages],
  );

  const themeOptions = useMemo(() => {
    if (!appReady) return {};
    const themeKey = stylesPatientAppRedesign?.styles?.theme?.key || EThemeKey.Variant1;
    return getThemeOptions(themeKey, stylesPatientAppRedesign?.styles?.theme);
  }, [appReady, stylesPatientAppRedesign]);

  return (
    <UIModulesProvider
      updateToken={(token) => {
        dispatch(initApp.request({ token }));
      }}
      // Router
      routingMap={routingMap}
      // TODO ramon/daniel rename this prop to handleSnackbars
      onOpen={({ variant, message }) => {
        // TODO ramon/daniel accept only string on "message" prop
        dispatch(showMessage(String(message), variant || 'success'));
      }}
      navigate={({ hasAccess, modulePathname, params, debounced, target }) => {
        if (hasAccess) {
          const to = getNavigateTo(modulePathname, params);
          if (debounced) return to;

          // Ensure we open absolute URLs on a new blank.
          // E.g. for patient-web-app URLs
          if (modulePathname && /^http/i.test(modulePathname)) {
            // Note that we can't use `to.pathname` here bc it has double `//` replaced by `/`
            const url = `${modulePathname}${to?.search || ''}`;
            if (target === '_self') {
              window.location.assign(url);
            } else {
              window.open(url, '_blank');
            }
          } else {
            browserHistory.push(to);
          }
        }
      }}
      navigateBack={() => browserHistory.goBack()}
      // UIComponents
      messages={messages}
      // UICore
      themeOptions={themeOptions}
      // UIModel
      env={env}
      keyEnv={keyEnv}
      keyAuthToken={KEYS.token}
      // UIUtils
      logger={undefined}
      // @ts-ignore
      config={config}
      locale={locale}
      setLocale={(newLocale) => dispatch(changeLocale(newLocale as any))}
      axiosClient={axiosClient}
    >
      <AuthHandler />
      {children}
    </UIModulesProvider>
  );
}
